import { type Theme, useMediaQuery } from '@mui/material';
import { type ReactNode, useId, useState } from 'react';

export type ComparisonMatrixItem = {
  id: string;
  /** Name of the compared item (e.g. the provider of a quote). */
  title: string;
  /** Secondary facts under the title: dates, state `Tag`, document link. */
  meta?: ReactNode;
  /** Resource menu of the item. */
  action?: ReactNode;
};

export type ComparisonMatrixCriterion = {
  id: string;
  label: string;
  /** Amounts and quantities: emphasized, tabular numerals, right-aligned in the matrix. */
  numeric?: boolean;
  /** Value of each item, keyed by item id. Missing values show `emptyValue`. */
  values: Record<string, ReactNode | null | undefined>;
};

export type ComparisonMatrixLayout = 'auto' | 'matrix' | 'deck';

export type ComparisonMatrixProps = {
  'aria-label': string;
  items: ComparisonMatrixItem[];
  criteria: ComparisonMatrixCriterion[];
  /** Header of the criteria column. */
  criteriaLabel?: string;
  /** Criterion whose value stays visible on a collapsed card (e.g. the price). */
  summaryCriterionId?: string;
  /** Shown for a missing value. */
  emptyValue?: string;
  /** `auto`: matrix from `md`, card deck below. */
  layout?: ComparisonMatrixLayout;
  'data-testid'?: string;
};

const DEFAULT_EMPTY_VALUE = '—';

function isBelowMd(theme: Theme) {
  return theme.breakpoints.down('md');
}

function hasValue(value: ReactNode | null | undefined) {
  return value !== null && value !== undefined && value !== '';
}

export function useComparisonMatrix(props: ComparisonMatrixProps) {
  const baseId = useId();
  const isCompactViewport = useMediaQuery(isBelowMd, { noSsr: true });
  const layout = props.layout ?? 'auto';
  const isDeck = layout === 'deck' || (layout === 'auto' && isCompactViewport);
  const [expandedIds, setExpandedIds] = useState<string[]>(() => (
    props.items.length > 0 ? [props.items[0].id] : []
  ));

  const emptyValue = props.emptyValue ?? DEFAULT_EMPTY_VALUE;
  const summaryCriterion = props.criteria.find((criterion) => criterion.id === props.summaryCriterionId);

  function isExpanded(itemId: string) {
    return expandedIds.includes(itemId);
  }

  function toggleItem(itemId: string) {
    setExpandedIds((current) => (
      current.includes(itemId) ? current.filter((id) => id !== itemId) : [...current, itemId]
    ));
  }

  function renderValue(value: ReactNode | null | undefined) {
    return hasValue(value) ? value : emptyValue;
  }

  function getPanelId(itemId: string) {
    return `${baseId}-${itemId}`;
  }

  return {
    isDeck,
    criteriaLabel: props.criteriaLabel ?? 'Critères',
    summaryCriterion,
    isExpanded,
    toggleItem,
    renderValue,
    getPanelId,
  };
}
